import { useState, type RefObject } from "react";

import type { OrgSessionsReportFilters } from "@/lib/api";
import { useT } from "@/app/localization";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { exportOrgSessionsCsv } from "@/views/orgSessionsCsvExport";

/** A refused export waiting for its currency: the request exactly as it was refused. */
export interface CsvCurrencyChoice {
  readonly orgId: number;
  readonly filters: Readonly<OrgSessionsReportFilters>;
  readonly currencies: readonly string[];
}

/**
 * The choice after the server refused a CSV whose selection spans currencies. Nothing is
 * sent until a code is clicked, even with one choice; that click repeats the refused
 * request for that one currency. A failure leaves the dialog open to choose again or cancel.
 */
export function OrgSessionsCsvCurrencyDialog({
  choice, returnFocusTo, onClose,
}: {
  choice: CsvCurrencyChoice | null;
  /** The export button that opened the choice: this dialog has no trigger of its own. */
  returnFocusTo: RefObject<HTMLElement | null>;
  onClose: () => void;
}) {
  const t = useT();
  const [exporting, setExporting] = useState(false);
  if (!choice) return null;

  const exportCurrency = async (currency: string) => {
    setExporting(true);
    try {
      if ((await exportOrgSessionsCsv(t, choice.orgId, choice.filters, currency)) === "saved") onClose();
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !exporting) onClose(); }}>
      <DialogContent
        showCloseButton={false}
        data-testid="org-sessions-csv-currency-dialog"
        // Escape belongs to this dialog alone: the organization panel behind it also closes on it.
        onEscapeKeyDown={(event) => event.stopPropagation()}
        // Radix would aim at its missing trigger and leave focus on the page. This runs after
        // the close has rendered, when the opener no longer sits behind a modal.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusTo.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("operator.orgs.report.csv_choose_currency_title")}</DialogTitle>
          <DialogDescription>{t("operator.orgs.report.csv_mixed_currency")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {choice.currencies.map((currency) => (
            <Button key={currency} variant="outline" disabled={exporting} onClick={() => exportCurrency(currency)}>
              {currency}
            </Button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" disabled={exporting} onClick={onClose}>{t("common.action.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
